<template>
  <div class="flex h-auto bg-gray-900 text-white">
    <aside class="w-64 bg-gray-800 p-6 flex flex-col space-y-6 shadow-lg bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700">
      <div class="flex items-center justify-center space-x-3">
       <img src="@/assets/logo.svg" alt="Vultric Logo" class="h-20">
      </div>
      <Navbar />
    </aside>

    <div class="flex-1 flex flex-col">
      <Header></Header>

      <main class="p-6 flex-1">
        <div class="p-6 bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700 
        rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition">
          <h3 class="text-lg font-semibold mb-4">Account Settings</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="p-4 bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700 
            rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition">
              <h4 class="text-md font-semibold">Profile Information</h4>
              <input v-model="firstName" type="text" placeholder="First Name" class="w-full p-2 rounded bg-gray-600 mt-2">
              <input v-model="lastName" type="text" placeholder="Last Name" class="w-full p-2 rounded bg-gray-600 mt-2">
              <input v-model="email" type="email" placeholder="Email" class="w-full p-2 rounded bg-gray-600 mt-2">
              <button @click="saveProfile" class="mt-2 bg-blue-500 p-2 rounded-lg hover:bg-blue-600">Save Changes</button>
            </div>

            <div class="p-4 bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700 
            rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition">
              <h4 class="text-md font-semibold">Password</h4>
              <input v-model="currentPassword" type="password" placeholder="Current Password" class="w-full p-2 rounded bg-gray-600 mt-2">
              <input v-model="newPassword" type="password" placeholder="New Password" class="w-full p-2 rounded bg-gray-600 mt-2">
              <button @click="updatePassword" class="mt-2 bg-blue-500 p-2 rounded-lg hover:bg-blue-600">Update Password</button>
            </div>
          </div>
        </div>

        <!-- Security Settings (2FA) -->
        <div class="p-6 mt-6 bg-gradient-to-b from-gray-800 to-gray-900 border border-gray-700 
        rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition">
          <h3 class="text-lg font-semibold mb-4">Security Settings</h3>
          <div class="bg-gray-700 p-4 rounded-lg shadow">
            <h4 class="text-md font-semibold">Two-Factor Authentication</h4>
            <p class="text-sm text-gray-300">Enhance your account security by enabling 2FA.</p>
            
            <div>
              <button v-if="!is2FAEnabled" @click="enable2FA" class="mt-2 bg-blue-500 p-2 rounded-lg hover:bg-blue-600">
                Enable 2FA
              </button>

              <button v-if="is2FAEnabled" @click="disable2FA" class="mt-2 bg-red-500 p-2 rounded-lg hover:bg-red-600">
                Disable 2FA
              </button>
            </div>
            <div v-if="isQRReady">
              <img v-if="qrCode" :src="qrCode" alt="2FA QR Code" class="w-32 h-32 mx-auto mt-3">
              <input v-if="qrCode" v-model="verificationCode" type="text" placeholder="Enter 6-digit code" class="w-full p-2 rounded bg-gray-600 mt-2">
              <button v-if="qrCode" @click="verify2FA" class="mt-2 bg-green-500 p-2 rounded-lg hover:bg-green-600">Verify & Activate</button>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>

    <!-- 2FA / Toast -->
    <TwoFAModal v-if="is2FANeeded" :isOpen="is2FANeeded" @close="is2FANeeded = false" @verify="password2FA" />
    <TwoFAModal v-if="isInfo2FA" :isOpen="isInfo2FA" @close="isInfo2FA = false" @verify="userInfo2FA" />
  <ToastManager ref="toastManager" />
</template>

<script>
import axios from 'axios';
import Navbar from '@/components/nav.vue';
import Header from '@/components/header.vue';
import ToastManager from '@/components/ToastManager.vue';
import TwoFAModal from '@/components/TwoFAModal.vue';

export default {
  name: "CloudSettings",
  data() {
    return {
      firstName: "",
      lastName: "",
      email: "",
      currentPassword: "",
      newPassword: "",
      is2FAEnabled: false,
      isQRReady: false,
      is2FANeeded: false,
      isInfo2FA: false,
      qrCode: null,
      verificationCode: ""
    };
  },
  components: {
    Navbar,
    Header,
    ToastManager,
    TwoFAModal
  },
  methods: {
    async fetchUserData() {
      try {
        const response = await axios.get('https://api.syniac.com:3000/api/users/user', { withCredentials: true });
        const { name, email } = response.data;

        this.email = email;

        const nameParts = name.split(" ");
        this.firstName = nameParts[0] || "";
        this.lastName = nameParts.slice(1).join(" ") || "";
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    },
    async saveProfile() {
      if (this.email.length > 0) {
        if (this.is2FAEnabled) {
          this.isInfo2FA = true;
        } else {
          const updatedFullName = this.firstName + ' ' + this.lastName.trim();  // Fixed concatenation
          await axios.post('https://api.syniac.com:3000/api/users/update', { full_name: updatedFullName, email: this.email }, { withCredentials: true });
          this.$refs.toastManager.addToast('User info changed successfully!', 'success');     
        }
      } else {
        this.$refs.toastManager.addToast('Missing information in user info.', 'error');  
      }
    },
    async updatePassword() {
      if (!this.currentPassword || !this.newPassword) {
        this.$refs.toastManager.addToast('Enter your current and new password.', 'error');
        return;
      }
      if (this.is2FAEnabled) {
        this.is2FANeeded = true;
      } else {
        await axios.post('https://api.syniac.com:3000/api/users/change-password', { currentPassword: this.currentPassword, newPassword: this.newPassword }, { withCredentials: true });
        this.$refs.toastManager.addToast('Password changed successfully!', 'success');
      }
    },
    async fetch2FAStatus() {
      try {
        const response = await axios.get('https://api.syniac.com:3000/api/2fa/status', { withCredentials: true });
        console.log(response.data)
        this.is2FAEnabled = response.data.enabled;
      } catch (error) {
        console.error("Error fetching 2FA status:", error);
      }
    },
    async enable2FA() {
      try {
        const response = await axios.post('https://api.syniac.com:3000/api/2fa/enable', {}, { withCredentials: true });
        this.qrCode = response.data.qrCode;
        this.secretKey = response.data.secret;
        this.isQRReady = true;
      } catch (error) {
        console.error("Error enabling 2FA:", error);
      }
    },
    async verify2FA() {
      try {
        await axios.post('https://api.syniac.com:3000/api/2fa/verify', { otp: this.verificationCode }, { withCredentials: true });
        this.$refs.toastManager.addToast('2FA setup successfully!', 'success');
        this.isQRReady = false;
        this.is2FANeeded = false;
        this.is2FAEnabled = true;
      } catch (error) {
        console.error("Error verifying 2FA:", error);
      }
    },
    async password2FA(code) {
      try {
        await axios.post('https://api.syniac.com:3000/api/2fa/verify', { otp: code }, { withCredentials: true });
        await axios.post('https://api.syniac.com:3000/api/users/change-password', { currentPassword: this.currentPassword, newPassword: this.newPassword }, { withCredentials: true });
        this.$refs.toastManager.addToast('Password changed successfully!', 'success');
        this.isQRReady = false;
        this.is2FANeeded = false;
        this.is2FAEnabled = true;
      } catch (error) {
        this.$refs.toastManager.addToast(`Error changing password.`, 'error');
        console.error("Error with changing password:", error);
      }
    },
    async userInfo2FA(code) {
      try {
        await axios.post('https://api.syniac.com:3000/api/2fa/verify', { otp: code }, { withCredentials: true });
        const updatedFullName = this.firstName + ' ' + this.lastName.trim();  // Fixed concatenation
        await axios.post('https://api.syniac.com:3000/api/users/update', { full_name: updatedFullName, email: this.email }, { withCredentials: true });
        this.$refs.toastManager.addToast('User info changed successfully!', 'success');
        this.isQRReady = false;
        this.isInfo2FA = false;
        this.is2FAEnabled = true;
      } catch (error) {
        this.$refs.toastManager.addToast('Error changing user info.', 'error');
        console.error("Error with changing user info:", error)
      }
    },
    async disable2FA() {
      try {
        await axios.post('https://api.syniac.com:3000/api/2fa/disable', {}, { withCredentials: true });
        this.is2FAEnabled = false;
        this.qrCode = "";
        this.secretKey = "";
      } catch (error) {
        console.error("Error disabling 2FA:", error);
      }
    }
  },
  mounted() {
    this.fetch2FAStatus();
    this.fetchUserData();
  }
};
</script>
